import { ArrowRightIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useState } from 'react'
import { createPortal } from 'react-dom'

import { CensusUpload } from 'components/quoting/quotes/CensusUpload'
import { useEmployer } from 'pages/employer_onboarding/EmployerProvider'
import { Button, FileList, TextButton } from 'slab'
import { type QuotingEmployeeCensus } from 'utils/web_api.schemas'

interface CSVUploadProps {
  buttonPortalContainer: HTMLElement | null;
  census?: QuotingEmployeeCensus;
  onSuccess: (censusId: string) => void;
}

function censusStatusToFileStatus (status: QuotingEmployeeCensus['status']) {
  switch (status) {
    case 'unprocessed':
    case 'processing':
      return 'uploading'
    case 'processed':
      return 'success'
    case 'failed':
      return 'error'
  }
}

export function CSVUpload ({
  buttonPortalContainer,
  census,
  onSuccess,
}: CSVUploadProps) {
  const [uploadNewCensus, setUploadNewCensus] = useState(false)

  const prospectId = useEmployer()?.employer?.quoting_prospect_id ?? ''

  function renderParsingErrors (
    errors: QuotingEmployeeCensus['parsing_errors']
  ) {
    if (!errors) return undefined

    return errors.map((error, index) => <div key={index}>{error}</div>)
  }

  function renderContent () {
    if (!census || uploadNewCensus) {
      return (
        <div>
          <div className='flex items-center justify-between mb-3'>
            <h2 className='body-sm-bold'>Upload new census</h2>
            {census && (
              <div className='body-sm'>
                <TextButton
                  onClick={() => {
                    setUploadNewCensus(false)
                  }}
                >
                  Cancel
                </TextButton>
              </div>
            )}
          </div>
          <CensusUpload
            prospectId={prospectId}
            onCensusProcessed={async (censusId) => {
              onSuccess(censusId)
            }}
          />
        </div>
      )
    }

    return (
      <div>
        <div className='flex items-center justify-between mb-3'>
          <h2 className='body-sm-bold'>Uploaded census</h2>
          <div className='body-sm'>
            <TextButton
              onClick={() => {
                setUploadNewCensus(true)
              }}
            >
              Upload new census
            </TextButton>
          </div>
        </div>
        <FileList>
          <FileList.File
            aria-label='Census file'
            name={census.file_name}
            status={censusStatusToFileStatus(census.status)}
            size={census.file_size}
            errorMessage={renderParsingErrors(census.parsing_errors)}
          />
        </FileList>
      </div>
    )
  }

  function renderNextButton () {
    if (buttonPortalContainer) {
      return createPortal(
        <Button
          disabled={!census || census.status !== 'processed' || uploadNewCensus}
          onClick={() => {
            if (census) onSuccess(census.id)
          }}
          rightIcon={ArrowRightIcon}
          size='large'
          variant='primary'
        >
          Next step
        </Button>,
        buttonPortalContainer
      )
    }

    return null
  }

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {renderContent()}
      </motion.div>
      {renderNextButton()}
    </>
  )
}
